import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WHOLE_GROUP_FILTER } from "services/posts.service";

interface FeedFilterProps {
  /** Companies of the viewer's group, in the order they are listed. */
  companies: { id: string; name: string }[];
  /** WHOLE_GROUP_FILTER or the id of one company. */
  value: string;
  onChange: (value: string) => void;
}

/** Chooses whose posts the feed shows: the whole group or one company. */
export function FeedFilter({ companies, value, onChange }: FeedFilterProps) {
  const { t } = useTranslation();
  const options = [
    { id: WHOLE_GROUP_FILTER, name: t("feed_filter_whole_group") },
    ...companies,
  ];
  const selected = options.find((option) => option.id === value) ?? options[0];

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-600">{t("feed_filter_label")}</span>
      <Listbox value={value} onChange={onChange}>
        <div className="relative">
          <ListboxButton
            aria-label={t("feed_filter_label")}
            className="flex min-w-[10rem] items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 data-[hover]:bg-gray-50"
          >
            <span className="truncate">{selected.name}</span>
            <ChevronDown className="h-4 w-4 flex-shrink-0 text-gray-500" />
          </ListboxButton>
          <ListboxOptions className="absolute left-0 z-20 mt-1 max-h-64 w-max min-w-full overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
            {options.map((option) => (
              <ListboxOption
                key={option.id}
                value={option.id}
                className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-900 data-[focus]:bg-gray-100"
              >
                <Check
                  className={`h-4 w-4 flex-shrink-0 text-blue-600 ${
                    option.id === value ? "" : "invisible"
                  }`}
                />
                <span>{option.name}</span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>
    </div>
  );
}
